package ai.wagemix.regression

import ai.wagemix.net.models.*
import kotlin.math.roundToInt

/**
 * Adds projected years to the stats collection using linear regression
 * @param count Number of years to project forward (1-10)
 * @param regressionType Type of regression to use (currently only Linear is supported)
 * @return New WMStatsCollection with projected years added
 */
fun WMStatsCollection.addYears(
    count: Int,
    regressionType: WMRegressionType = WMRegressionType.Linear
): WMStatsCollection {
    if (count <= 0) return this
    if (count > 10) throw IllegalArgumentException("Cannot project more than 10 years")

    // Determine the last actual year from existing data
    val existingMetadata = this.regressionMetadata
    val lastActualYear = existingMetadata?.lastActualYear ?: (endYear ?: return this)
    val existingProjectedYears = existingMetadata?.projectedYears ?: emptySet()


    // Build new occupation stats with projected data
    val newOccupationStats = orderedStats().associate { (occupationId, occupationStats) ->
        val historicalYears = occupationStats.years

        if (historicalYears.isEmpty()) {
            return@associate occupationId to occupationStats
        }

        // Extract historical data points for each metric
        val annualP10Points = mutableListOf<Pair<Int, Double>>()
        val annualP25Points = mutableListOf<Pair<Int, Double>>()
        val annualMedianPoints = mutableListOf<Pair<Int, Double>>()
        val annualP75Points = mutableListOf<Pair<Int, Double>>()
        val annualP90Points = mutableListOf<Pair<Int, Double>>()
        val annualMeanPoints = mutableListOf<Pair<Int, Double>>()
        val hourlyP10Points = mutableListOf<Pair<Int, Double>>()
        val hourlyP25Points = mutableListOf<Pair<Int, Double>>()
        val hourlyMedianPoints = mutableListOf<Pair<Int, Double>>()
        val hourlyP75Points = mutableListOf<Pair<Int, Double>>()
        val hourlyP90Points = mutableListOf<Pair<Int, Double>>()
        val hourlyMeanPoints = mutableListOf<Pair<Int, Double>>()

        historicalYears.forEach { year ->
            // Only use actual historical data, not existing projections
            if (year <= lastActualYear) {
                occupationStats.statForYear(year)?.let { stat ->
                    stat.annualP10?.let { annualP10Points.add(year to it.toDouble()) }
                    stat.annualP25?.let { annualP25Points.add(year to it.toDouble()) }
                    stat.annualMedian?.let { annualMedianPoints.add(year to it.toDouble()) }
                    stat.annualP75?.let { annualP75Points.add(year to it.toDouble()) }
                    stat.annualP90?.let { annualP90Points.add(year to it.toDouble()) }
                    stat.annualMean?.let { annualMeanPoints.add(year to it.toDouble()) }
                    stat.hourlyP10?.let { hourlyP10Points.add(year to it.toDouble()) }
                    stat.hourlyP25?.let { hourlyP25Points.add(year to it.toDouble()) }
                    stat.hourlyMedian?.let { hourlyMedianPoints.add(year to it.toDouble()) }
                    stat.hourlyP75?.let { hourlyP75Points.add(year to it.toDouble()) }
                    stat.hourlyP90?.let { hourlyP90Points.add(year to it.toDouble()) }
                    stat.hourlyMean?.let { hourlyMeanPoints.add(year to it.toDouble()) }
                }
            }
        }


        // Calculate regression for each metric
        val annualP10Regression = calculateLinearRegressionFormula(annualP10Points)
        val annualP25Regression = calculateLinearRegressionFormula(annualP25Points)
        val annualMedianRegression = calculateLinearRegressionFormula(annualMedianPoints)
        val annualP75Regression = calculateLinearRegressionFormula(annualP75Points)
        val annualP90Regression = calculateLinearRegressionFormula(annualP90Points)
        val annualMeanRegression = calculateLinearRegressionFormula(annualMeanPoints)
        val hourlyP10Regression = calculateLinearRegressionFormula(hourlyP10Points)
        val hourlyP25Regression = calculateLinearRegressionFormula(hourlyP25Points)
        val hourlyMedianRegression = calculateLinearRegressionFormula(hourlyMedianPoints)
        val hourlyP75Regression = calculateLinearRegressionFormula(hourlyP75Points)
        val hourlyP90Regression = calculateLinearRegressionFormula(hourlyP90Points)
        val hourlyMeanRegression = calculateLinearRegressionFormula(hourlyMeanPoints)


        // Project forward count years
        val projectedStats = (1..count).associate { offset ->
            val projectedYear = lastActualYear + offset
            val projectedStat = WMStatsCollection.OccupationStat.kxs(
                annualP10 = annualP10Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                annualP25 = annualP25Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                annualMedian = annualMedianRegression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                annualP75 = annualP75Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                annualP90 = annualP90Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                annualMean = annualMeanRegression?.predict(projectedYear)?.coerceAtLeast(0.0)?.roundToInt(),
                cappedFlags = 0, // Projected data has no capped flags
                hourlyP10 = hourlyP10Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                hourlyP25 = hourlyP25Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                hourlyMedian = hourlyMedianRegression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                hourlyP75 = hourlyP75Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                hourlyP90 = hourlyP90Regression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                hourlyMean = hourlyMeanRegression?.predict(projectedYear)?.coerceAtLeast(0.0)?.toFloat(),
                missingFlags = 0 // Projected data has no missing flags
            )
            projectedYear to projectedStat
        }

        // Combine historical + projected stats
        val combinedStats = historicalYears.associateWith { year ->
            occupationStats.statForYear(year)!!
        } + projectedStats

        val newOccupationStats = WMStatsCollection.OccupationStats.kxs(
            stats = combinedStats,
            title = occupationStats.title,
            weight = occupationStats.weight
        )

        occupationId to newOccupationStats
    }

    // Build new regression metadata
    val newProjectedYears = existingProjectedYears + (lastActualYear + 1..lastActualYear + count).toSet()
    val newMetadata = WMStatsCollection.RegressionMetadata.kxs(
        regressionType = regressionType,
        projectedYears = newProjectedYears,
        lastActualYear = lastActualYear
    )


    // Return new stats collection with projected data
    return WMStatsCollection.kxs(
        area = area,
        industry = industry,
        occupationStats = newOccupationStats,
        regressionMetadata = newMetadata
    )
}

/**
 * Backfills missing years in the stats collection using linear regression
 * If an occupation has gaps in years (e.g., missing 2020 but has 2021-2024), 
 * this will use the available data to estimate the missing values. An occupation with a
 * single year of data is held flat at that year's values.
 * 
 * @param targetYears The range of years that should exist (e.g., 2020..2024)
 * @param regressionType Type of regression to use (currently only Linear is supported)
 * @return New WMStatsCollection with backfilled missing years
 */
fun WMStatsCollection.backfillMissingYears(
    targetYears: IntRange,
    regressionType: WMRegressionType = WMRegressionType.Linear
): WMStatsCollection {
    
    val backfilledYears = mutableSetOf<Int>()
    
    // Process each occupation
    val newOccupationStats = orderedStats().associate { (occupationId, occupationStats) ->
        val existingYears = occupationStats.years.toSet()
        val missingYears = targetYears.filter { it !in existingYears }
        
        if (missingYears.isEmpty()) {
            return@associate occupationId to occupationStats
        }
        
        
        // Collect data points for regression
        val annualP10Points = mutableListOf<Pair<Int, Double>>()
        val annualP25Points = mutableListOf<Pair<Int, Double>>()
        val annualMedianPoints = mutableListOf<Pair<Int, Double>>()
        val annualP75Points = mutableListOf<Pair<Int, Double>>()
        val annualP90Points = mutableListOf<Pair<Int, Double>>()
        val annualMeanPoints = mutableListOf<Pair<Int, Double>>()
        val hourlyP10Points = mutableListOf<Pair<Int, Double>>()
        val hourlyP25Points = mutableListOf<Pair<Int, Double>>()
        val hourlyMedianPoints = mutableListOf<Pair<Int, Double>>()
        val hourlyP75Points = mutableListOf<Pair<Int, Double>>()
        val hourlyP90Points = mutableListOf<Pair<Int, Double>>()
        val hourlyMeanPoints = mutableListOf<Pair<Int, Double>>()
        
        // Collect existing data points
        existingYears.sorted().forEach { year ->
            occupationStats.statForYear(year)?.let { stat ->
                stat.annualP10?.let { annualP10Points.add(year to it.toDouble()) }
                stat.annualP25?.let { annualP25Points.add(year to it.toDouble()) }
                stat.annualMedian?.let { annualMedianPoints.add(year to it.toDouble()) }
                stat.annualP75?.let { annualP75Points.add(year to it.toDouble()) }
                stat.annualP90?.let { annualP90Points.add(year to it.toDouble()) }
                stat.annualMean?.let { annualMeanPoints.add(year to it.toDouble()) }
                stat.hourlyP10?.let { hourlyP10Points.add(year to it.toDouble()) }
                stat.hourlyP25?.let { hourlyP25Points.add(year to it.toDouble()) }
                stat.hourlyMedian?.let { hourlyMedianPoints.add(year to it.toDouble()) }
                stat.hourlyP75?.let { hourlyP75Points.add(year to it.toDouble()) }
                stat.hourlyP90?.let { hourlyP90Points.add(year to it.toDouble()) }
                stat.hourlyMean?.let { hourlyMeanPoints.add(year to it.toDouble()) }
            }
        }
        
        // Calculate regression formulas
        val annualP10Regression = calculateLinearRegressionFormula(annualP10Points)
        val annualP25Regression = calculateLinearRegressionFormula(annualP25Points)
        val annualMedianRegression = calculateLinearRegressionFormula(annualMedianPoints)
        val annualP75Regression = calculateLinearRegressionFormula(annualP75Points)
        val annualP90Regression = calculateLinearRegressionFormula(annualP90Points)
        val annualMeanRegression = calculateLinearRegressionFormula(annualMeanPoints)
        val hourlyP10Regression = calculateLinearRegressionFormula(hourlyP10Points)
        val hourlyP25Regression = calculateLinearRegressionFormula(hourlyP25Points)
        val hourlyMedianRegression = calculateLinearRegressionFormula(hourlyMedianPoints)
        val hourlyP75Regression = calculateLinearRegressionFormula(hourlyP75Points)
        val hourlyP90Regression = calculateLinearRegressionFormula(hourlyP90Points)
        val hourlyMeanRegression = calculateLinearRegressionFormula(hourlyMeanPoints)
        
        // Backfill missing years
        val backfilledStats = mutableMapOf<Int, WMStatsCollection.OccupationStat>()
        missingYears.forEach { year ->
            backfilledYears.add(year)
            backfilledStats[year] = WMStatsCollection.OccupationStat.kxs(
                annualP10 = annualP10Regression?.let { (it.slope * year + it.intercept).roundToInt() },
                annualP25 = annualP25Regression?.let { (it.slope * year + it.intercept).roundToInt() },
                annualMedian = annualMedianRegression?.let { (it.slope * year + it.intercept).roundToInt() },
                annualP75 = annualP75Regression?.let { (it.slope * year + it.intercept).roundToInt() },
                annualP90 = annualP90Regression?.let { (it.slope * year + it.intercept).roundToInt() },
                annualMean = annualMeanRegression?.let { (it.slope * year + it.intercept).roundToInt() },
                cappedFlags = 0,
                hourlyP10 = hourlyP10Regression?.let { (it.slope * year + it.intercept).toFloat() },
                hourlyP25 = hourlyP25Regression?.let { (it.slope * year + it.intercept).toFloat() },
                hourlyMedian = hourlyMedianRegression?.let { (it.slope * year + it.intercept).toFloat() },
                hourlyP75 = hourlyP75Regression?.let { (it.slope * year + it.intercept).toFloat() },
                hourlyP90 = hourlyP90Regression?.let { (it.slope * year + it.intercept).toFloat() },
                hourlyMean = hourlyMeanRegression?.let { (it.slope * year + it.intercept).toFloat() },
                missingFlags = 0 // These are backfilled, not missing
            )
        }
        
        // Combine existing and backfilled stats
        val allStats = (existingYears.associateWith { year -> occupationStats.statForYear(year)!! } + backfilledStats)
        
        val newOccupationStats = WMStatsCollection.OccupationStats.kxs(
            stats = allStats,
            title = occupationStats.title,
            weight = occupationStats.weight
        )
        
        occupationId to newOccupationStats
    }
    
    // Add metadata about backfilled years - MERGE with existing metadata instead of replacing
    val existingMetadata = regressionMetadata
    val newMetadata = if (backfilledYears.isNotEmpty() || existingMetadata != null) {
        val existingProjectedYears = existingMetadata?.projectedYears ?: emptySet()
        val existingLastActualYear = existingMetadata?.lastActualYear
        
        // Merge backfilled years with existing projected years
        val allProjectedYears = backfilledYears + existingProjectedYears
        
        // Use the minimum lastActualYear between existing and backfill boundary
        // This ensures we keep the original actual data boundary
        val lastActual = if (existingLastActualYear != null) {
            // If we had existing metadata with projections, keep that boundary
            existingLastActualYear
        } else {
            // If no existing metadata, use the backfill boundary
            targetYears.last
        }
        
        WMStatsCollection.RegressionMetadata.kxs(
            regressionType = existingMetadata?.regressionType ?: regressionType,
            projectedYears = allProjectedYears,
            lastActualYear = lastActual
        )
    } else {
        null
    }
    
    return WMStatsCollection.kxs(
        area = area,
        industry = industry,
        occupationStats = newOccupationStats,
        regressionMetadata = newMetadata
    )
}
